import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Role } from '@data/sdk/match';
import { enabledPrototypeRoutes } from '@prototypes/registry';
import { useTheme } from '@ui/hooks';

import { paths } from './constants';
import PanelsAccessLayout from './pages/b2b/Panels/pages/components/PanelsAccessLayout';
import QuestionPage from './pages/b2b/QuestionsLibrary/pages/[id]';
import QuestionClassificationPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification';
import QuestionIcdCodesPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/IcdCodes';
import QuestionStartersPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/Starters';
import QuestionTagsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Classification/pages/Tags';
import QuestionDetailsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Details';
import QuestionMergePage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Merge';
import QuestionTrialsPage from './pages/b2b/QuestionsLibrary/pages/[id]/pages/Trials';
import { trialPageLoader } from './pages/b2b/TrialRecruitment/TrialPage';
import TrialsLibraryPage from './pages/b2b/TrialsLibrary';
import TrialDetailsPage from './pages/b2b/TrialsLibrary/pages/[id]';
import { trialsRedesignLoader } from './pages/b2b/TrialsRedesign/loader';
import { FixedLayout, Layout } from './pages/patient/components';
import { ProfileLayout } from './pages/patient/Profile/components';
import { PublicPageLayout, PublicPageLayoutV2 } from './pages/public';

import { lazyWithRetry, PrivateRoute, queryClient } from './utils';
import { features } from './utils/feature-flags';

// Shared pages
const Notifications = lazyWithRetry(
  () => import('./pages/shared/Notifications/Notifications'),
  'Notifications',
);

// Patient profile router
const GeneralPage = lazyWithRetry(() => import('./pages/patient/Profile/General'), 'GeneralPage');
const PasswordPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Password'),
  'PasswordPage',
);
const DiseasesPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Diseases'),
  'DiseasesPage',
);
const AttributesPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Attributes'),
  'AttributesPage',
);
const PatientNotificationsSettingsPage = lazyWithRetry(
  () => import('./pages/patient/Profile/Notifications'),
  'PatientNotificationsSettingsPage',
);
const AccountPage = lazyWithRetry(() => import('./pages/patient/Profile/Account'), 'AccountPage');

// Patient router
const AccountProgressionPage = lazyWithRetry(
  () => import('./pages/patient/AccountProgression'),
  'AccountProgressionPage',
);
const ChatPage = lazyWithRetry(() => import('./pages/shared/Chat'), 'ChatPage');
const DeleteAccount = lazyWithRetry(
  () => import('./pages/patient/Profile/DeleteAccount'),
  'DeleteAccount',
);
const TrialsListPage = lazyWithRetry(() => import('./pages/patient/Trials/List'), 'TrialsListPage');
const TrialsListV2Page = lazyWithRetry(
  () => import('./pages/patient/Trials/ListV2'),
  'TrialsListV2Page',
);
const TrialPage = lazyWithRetry(() => import('./pages/patient/Trials/[id]'), 'TrialPage');
const PatientSurveyPage = lazyWithRetry(
  () => import('./pages/patient/Trials/[surveyId]'),
  'PatientSurveyPage',
);
const PatientPanelPage = lazyWithRetry(
  () => import('./pages/patient/Trials/[panelId]'),
  'PatientPanelPage',
);

// B2B trial recruitment router
const B2BTrialsPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage'),
  'B2BTrialsPage',
);
const B2BTrialsListPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/TrialsList'),
  'B2BTrialsListPage',
);
const RequestsListPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/RequestsList'),
  'RequestsListPage',
);
const BulkInvitesList = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialsListPage/BulkInvitesList'),
  'BulkInvitesList',
);
const BulkInvitePage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/BulkInvite'),
  'BulkInvitePage',
);
const RequestTrialPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/RequestTrialPage'),
  'RequestTrialPage',
);
const EligibilityCriteriaPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/EligibilityCriteria'),
  'EligibilityCriteriaPage',
);
const MatchingVolunteersPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/MatchingVolunteers'),
  'MatchingVolunteersPage',
);
const VolunteerDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/MatchingVolunteers/VolunteerDetails'),
  'VolunteerDetailsPage',
);
const AdminNotificationSettings = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Notifications'),
  'AdminNotificationSettings',
);

const AdminTrialTeams = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Teams'),
  'AdminTrialTeams',
);

const MilestonesPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Milestones'),
  'MilestonesPage',
);

const MilestoneCreatePage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Milestones/pages/Create'),
  'MilestoneCreatePage',
);

const MilestoneDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Milestones/pages/[id]'),
  'MilestoneDetailsPage',
);

const TrialUpdatesPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage/Updates'),
  'TrialUpdatesPage',
);

// B2B profile router
const GeneralFormPage = lazyWithRetry(
  () => import('./pages/b2b/Profile/GeneralForm'),
  'GeneralFormPage',
);
const PasswordFormPage = lazyWithRetry(
  () => import('./pages/b2b/Profile/PasswordForm'),
  'PasswordFormPage',
);

// B2B hospitals router
const HospitalsListPage = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/List'),
  'HospitalsListPage',
);
const HospitalDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/[id]'),
  'HospitalDetailsPage',
);
const CreateHospital = lazyWithRetry(
  () => import('./pages/b2b/Hospitals/Create'),
  'CreateHospital',
);

// B2B diseases router
const DiseasesListPage = lazyWithRetry(
  () => import('./pages/b2b/Diseases/List'),
  'DiseasesListPage',
);
const DiseaseDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Diseases/[id]'),
  'DiseaseDetailsPage',
);

// B2B router
const B2BLayout = lazyWithRetry(() => import('./pages/b2b/components/B2BLayout'), 'B2BLayout');
const CreateTeam = lazyWithRetry(() => import('./pages/b2b/Team/Create'), 'CreateTeam');
const B2BProfileLayout = lazyWithRetry(
  () => import('./pages/b2b/Profile/components/ProfileLayout'),
  'B2BProfileLayout',
);
const TeamsList = lazyWithRetry(() => import('./pages/b2b/Team/TeamsList'), 'TeamsList');
const TeamSettingsLayout = lazyWithRetry(
  () => import('./pages/b2b/Team/TeamSettingsLayout'),
  'TeamSettingsLayout',
);
const TeamGeneral = lazyWithRetry(() => import('./pages/b2b/Team/TeamGeneral'), 'TeamGeneral');
const TeamUsers = lazyWithRetry(() => import('./pages/b2b/Team/TeamUsers'), 'TeamUsers');
const TeamBilling = lazyWithRetry(() => import('./pages/b2b/Team/TeamBilling'), 'TeamBilling');
const TeamPremium = lazyWithRetry(() => import('./pages/b2b/Team/PremiumLanding'), 'TeamPremium');
const TeamTrials = lazyWithRetry(() => import('./pages/b2b/Team/TeamTrials'), 'TeamTrials');
const TeamPanels = lazyWithRetry(() => import('./pages/b2b/Team/TeamPanels'), 'TeamPanels');
const TeamReportingScope = lazyWithRetry(
  () => import('./pages/b2b/Team/TeamReportingScope'),
  'TeamReportingScope',
);
const OnboardingPage = lazyWithRetry(() => import('./pages/b2b/Onboarding'), 'OnboardingPage');
const SurveyLanding = lazyWithRetry(() => import('./pages/b2b/SurveyLanding'), 'SurveyLanding');
const PartnerReportingPage = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting'),
  'PartnerReportingPage',
);
const PartnerReportingOverviewTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/LandingSection'),
  'PartnerReportingOverviewTab',
);
const PartnerReportingTrialsOverviewTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/TrialsOverviewSection'),
  'PartnerReportingTrialsOverviewTab',
);
const PartnerReportingTrialGeographyTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/TrialGeographySection'),
  'PartnerReportingTrialGeographyTab',
);
const PartnerReportingTrialCriteriaTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/TrialCriteriaSection'),
  'PartnerReportingTrialCriteriaTab',
);
const PartnerReportingPatientsOverviewTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/PatientsOverviewSection'),
  'PartnerReportingPatientsOverviewTab',
);
const PartnerReportingTrialRejectionsTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/TrialRejectionsSection'),
  'PartnerReportingTrialRejectionsTab',
);
const PartnerReportingSiteOpportunityTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/SiteOpportunitySection'),
  'PartnerReportingSiteOpportunityTab',
);
const PartnerReportingTrialDelaysTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/TrialDelaysSection'),
  'PartnerReportingTrialDelaysTab',
);
const PartnerReportingCriteriaSimulatorTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/CriteriaSimulatorSection'),
  'PartnerReportingCriteriaSimulatorTab',
);
const PartnerReportingChatbotTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/ChatbotSection'),
  'PartnerReportingChatbotTab',
);
const PartnerReportingFunnelTab = lazyWithRetry(
  () => import('./pages/b2b/PartnerReporting/components/UserFunnelSection'),
  'PartnerReportingFunnelTab',
);
const B2BTrialPage = lazyWithRetry(
  () => import('./pages/b2b/TrialRecruitment/TrialPage'),
  'B2BTrialPage',
);
const TrialsRedesignPage = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign'),
  'TrialsRedesignPage',
);
const TrialsRedesignOverviewTab = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign/pages/Overview'),
  'TrialsRedesignOverviewTab',
);
const TrialsRedesignCriteriaTab = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign/pages/Criteria'),
  'TrialsRedesignCriteriaTab',
);
const TrialsRedesignSitesTab = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign/pages/Sites'),
  'TrialsRedesignSitesTab',
);
const TrialsRedesignPatientsTab = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign/pages/Patients'),
  'TrialsRedesignPatientsTab',
);
const TrialsRedesignDetailsTab = lazyWithRetry(
  () => import('./pages/b2b/TrialsRedesign/pages/Details'),
  'TrialsRedesignDetailsTab',
);

// B2B panels router
const PanelsPage = lazyWithRetry(() => import('./pages/b2b/Panels'), 'PanelsPage');
const CreatePanelPage = lazyWithRetry(
  () => import('./pages/b2b/Panels/pages/Create'),
  'CreatePanelPage',
);
const PanelPage = lazyWithRetry(() => import('./pages/b2b/Panels/pages/[panelId]'), 'PanelPage');
const PanelDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Panels/pages/[panelId]/pages/Details'),
  'PanelDetailsPage',
);
const PanelInsightsPage = lazyWithRetry(
  () => import('./pages/b2b/Panels/pages/[panelId]/pages/Insights'),
  'PanelInsightsPage',
);
const PatientDetailsPage = lazyWithRetry(
  () => import('./pages/b2b/Panels/pages/[panelId]/pages/Insights/pages/[patientId]'),
  'PatientDetailsPage',
);

// B2B questions library router
const QuestionsLibraryPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary'),
  'QuestionsLibraryPage',
);
const QuestionActivityPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/[id]/pages/Activity'),
  'QuestionActivityPage',
);
const CreateQuestionPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/Create'),
  'CreateQuestionPage',
);
const QuestionMergeEditorPage = lazyWithRetry(
  () => import('./pages/b2b/QuestionsLibrary/pages/[id]/pages/Merge/pages/Editor'),
  'QuestionMergeEditorPage',
);
const PipelineListPage = lazyWithRetry(() => import('./pages/b2b/Pipeline'), 'PipelineListPage');
const PipelineTrialPage = lazyWithRetry(
  () => import('./pages/b2b/Pipeline/pages/PipelineTrial'),
  'PipelineTrialPage',
);
const PredicateQuestionsPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateQuestions'),
  'PredicateQuestionsPage',
);
const PredicateQuestionDetailPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateQuestions/pages/[questionId]'),
  'PredicateQuestionDetailPage',
);
const PredicateLibraryPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateLibrary'),
  'PredicateLibraryPage',
);
const PredicateLibraryBrowsePage = lazyWithRetry(
  () => import('./pages/b2b/PredicateLibrary/pages/Library'),
  'PredicateLibraryBrowsePage',
);
const PredicateLibraryItemDetailPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateLibrary/pages/Library/[predicateLibraryItemId]'),
  'PredicateLibraryItemDetailPage',
);
const PredicateLibraryTiersPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateLibrary/pages/Tiers'),
  'TiersPage',
);
const PredicateLibraryMissingPage = lazyWithRetry(
  () => import('./pages/b2b/PredicateLibrary/pages/Missing'),
  'MissingQueuePage',
);
const UnitsPage = lazyWithRetry(() => import('./pages/b2b/Units'), 'UnitsPage');
const UnitsStandardUnitsPage = lazyWithRetry(
  () => import('./pages/b2b/Units/pages/StandardUnits'),
  'UnitsStandardUnitsPage',
);
const UnitsStandardUnitDetailPage = lazyWithRetry(
  () => import('./pages/b2b/Units/pages/StandardUnits/[unit]'),
  'UnitsStandardUnitDetailPage',
);
const UnitsActiveRulesPage = lazyWithRetry(
  () => import('./pages/b2b/Units/pages/Active'),
  'UnitsActiveRulesPage',
);
const UnitsConversionQueuePage = lazyWithRetry(
  () => import('./pages/b2b/Units/pages/Queue'),
  'UnitsConversionQueuePage',
);
const UnitsRejectedRulesPage = lazyWithRetry(
  () => import('./pages/b2b/Units/pages/Rejected'),
  'UnitsRejectedRulesPage',
);

// B2B parity routing router
const ParityRoutingPage = lazyWithRetry(
  () => import('./pages/b2b/ParityRouting'),
  'ParityRoutingPage',
);
const ParityRoutingTablePage = lazyWithRetry(
  () => import('./pages/b2b/ParityRouting/pages/Routing'),
  'ParityRoutingTablePage',
);
const ParityRoutingHistoryPage = lazyWithRetry(
  () => import('./pages/b2b/ParityRouting/pages/History'),
  'ParityRoutingHistoryPage',
);

// B2B parity review router
const ParityReviewPage = lazyWithRetry(
  () => import('./pages/b2b/ParityReview'),
  'ParityReviewPage',
);
const ParityReviewSlicesPage = lazyWithRetry(
  () => import('./pages/b2b/ParityReview/pages/Slices'),
  'ParityReviewSlicesPage',
);
const ParityReviewTrialsPage = lazyWithRetry(
  () => import('./pages/b2b/ParityReview/pages/Trials'),
  'ParityReviewTrialsPage',
);
const ParityReviewMatchedSetsPage = lazyWithRetry(
  () => import('./pages/b2b/ParityReview/pages/MatchedSets'),
  'ParityReviewMatchedSetsPage',
);
const ParityReviewReadinessPage = lazyWithRetry(
  () => import('./pages/b2b/ParityReview/pages/Readiness'),
  'ParityReviewReadinessPage',
);

// B2B users router
const UsersPage = lazyWithRetry(() => import('./pages/b2b/Users'), 'UsersPage');

// Public router
const TrialInvitePage = lazyWithRetry(
  () => import('./pages/public/pages/TrialInvite'),
  'TrialInvitePage',
);
const TeamTrialInvitePage = lazyWithRetry(
  () => import('./pages/public/pages/TeamTrialInvite'),
  'TeamTrialInvitePage',
);
const SignInPage = lazyWithRetry(() => import('./pages/public/pages/SignIn'), 'SignInPage');
// const SignUpPage = lazyWithRetry(() => import('./pages/public/pages/PatientSignUp'), 'SignUpPage');
const SignUpV2Page = lazyWithRetry(
  () => import('./pages/public/pages/PatientSignUpV2'),
  'PatientSignUpV2Page',
);
const TrialManagerSignUpPage = lazyWithRetry(
  () => import('./pages/public/pages/TrialManagerSignUp'),
  'TrialManagerSignUpPage',
);
const SetupPasswordPage = lazyWithRetry(
  () => import('./pages/public/pages/SetupPassword'),
  'SetupPasswordPage',
);
const ForgotPasswordPage = lazyWithRetry(
  () => import('./pages/public/pages/ForgotPassword'),
  'ForgotPasswordPage',
);
const UnsubscribePage = lazyWithRetry(
  () => import('./pages/public/pages/Unsubscribe'),
  'UnsubscribePage',
);
const MilestoneInvitePage = lazyWithRetry(
  () => import('./pages/public/pages/MilestoneInvite'),
  'MilestoneInvitePage',
);
const AccountSelectPage = lazyWithRetry(
  () => import('./pages/public/pages/AccountSelect'),
  'AccountSelectPage',
);

// Embedded chat
const EmbeddedChatPage = lazyWithRetry(() => import('./pages/embedded-chat'), 'EmbeddedChatPage');
const EmbeddedChatV2Page = lazyWithRetry(
  () => import('./pages/embedded-chat-v2'),
  'EmbeddedChatV2Page',
);

// B2B surveys router
const SurveysPage = lazyWithRetry(() => import('./pages/b2b/Surveys'), 'SurveysPage');
const CreateSurveyPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/Create'),
  'CreateSurveyPage',
);
const SurveyPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]'),
  'SurveyPage',
);
const SurveyQuestionsPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]/pages/Questions'),
  'SurveyQuestionsPage',
);
const SurveyResponsesPage = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]/pages/Responses'),
  'SurveyResponsesPage',
);
const UpdateSurveyDetails = lazyWithRetry(
  () => import('./pages/b2b/Surveys/pages/[surveyId]/pages/Details'),
  'UpdateSurveyDetails',
);

// Rest of the imports
const NoMatchPage = lazyWithRetry(() => import('./pages/shared/NoMatch'), 'NoMatchPage');
const MaintenancePage = lazyWithRetry(
  () => import('./pages/shared/MaintenancePage'),
  'MaintenancePage',
);
const CTDIPage = lazyWithRetry(() => import('./pages/ctdi'), 'CTDIPage');

const patientProfileRouter = [
  { index: true, element: <Navigate to={paths.profile.general} /> },
  { path: paths.profile.general, element: <GeneralPage /> },
  { path: paths.profile.password, element: <PasswordPage /> },
  { path: paths.profile.diseases, element: <DiseasesPage /> },
  { path: paths.profile.attributes, element: <AttributesPage /> },
  { path: paths.profile.notifications, element: <PatientNotificationsSettingsPage /> },
  { path: paths.profile.account, element: <AccountPage /> },
];

const patientRouter = (isOptimisticSignUpInProgress: boolean) => {
  const { bgColorLight } = useTheme();

  return [
    {
      element: <Layout hideLinks />,
      children: [{ index: true, element: <AccountProgressionPage /> }],
    },
    {
      element: (
        <FixedLayout
          hideLinks={isOptimisticSignUpInProgress}
          contentStyle={{ backgroundColor: bgColorLight }}
        />
      ),
      children: [{ index: true, path: paths.chat, element: <ChatPage /> }],
    },
    {
      element: <Layout />,
      children: [{ index: true, path: paths.notifications, element: <Notifications /> }],
    },
    {
      element: <Layout />,
      children: [
        { path: paths.profile.root, element: <ProfileLayout />, children: patientProfileRouter },
        { path: paths.profile.delete_account, element: <DeleteAccount /> },
      ],
    },
    {
      element: <Layout />,
      children: [
        {
          path: paths.trials,
          children: [
            {
              index: true,
              element: features.myResearchV2 ? <TrialsListV2Page /> : <TrialsListPage />,
            },
            { path: ':trialId', element: <TrialPage /> },
            { path: `${paths.surveys.root}/:surveyId`, element: <PatientSurveyPage /> },
            { path: `${paths.panels.root}/:panelId`, element: <PatientPanelPage /> },
          ],
        },
      ],
    },
  ];
};

const b2bTrialRecruitmentRouter = (currentRole?: Role) => [
  { index: true, element: <Navigate to={paths.trials} /> },
  {
    element: <B2BTrialsPage />,
    children: [
      {
        path: paths.trials,
        element: <B2BTrialsListPage />,
      },
      ...(currentRole === Role.Admin
        ? [
            {
              path: paths.requests,
              element: <RequestsListPage />,
            },
            {
              path: paths.bulk_invites,
              children: [
                { index: true, element: <BulkInvitesList /> },
                {
                  path: paths.create,
                  element: <BulkInvitePage />,
                },
              ],
            },
          ]
        : []),
    ],
  },
  {
    path: `${paths.trials}/:trialId`,
    element: <B2BTrialPage />,
    loader: trialPageLoader(queryClient),
    children: [
      { element: <EligibilityCriteriaPage /> },
      { path: paths.criterias, element: <EligibilityCriteriaPage /> },
      {
        path: paths.onboarding.criteria_table,
        element: <EligibilityCriteriaPage isTableOverview />,
      },
      {
        path: paths.onboarding.trial_overview,
        element: <EligibilityCriteriaPage isDetailsOverview />,
      },
      { path: paths.volunteers, element: <MatchingVolunteersPage /> },
      ...(currentRole === Role.TrialManager || currentRole === Role.Sponsor
        ? [{ path: paths.notifications, element: <AdminNotificationSettings /> }]
        : []),
      ...(currentRole === Role.Admin ||
      currentRole === Role.TrialManager ||
      currentRole === Role.Sponsor
        ? [{ path: paths.updates, element: <TrialUpdatesPage /> }]
        : []),
      ...(currentRole === Role.Admin
        ? [
            { path: paths.teams, element: <AdminTrialTeams /> },
            { path: paths.milestones, element: <MilestonesPage /> },
          ]
        : []),
    ],
  },
  {
    path: `${paths.trials}/:trialId/${paths.milestones}/${paths.create}`,
    element: <MilestoneCreatePage />,
  },
  {
    path: `${paths.trials}/:trialId/${paths.milestones}/:milestoneId`,
    element: <MilestoneDetailsPage />,
  },
  { path: paths.request_trial, index: true, element: <RequestTrialPage /> },
  // Reviewers cannot read patient answers, so the page has nothing to show them.
  ...(currentRole === Role.Reviewer
    ? []
    : [
        {
          path: `${paths.trials}/:trialId/${paths.volunteers}/:volunteerId`,
          element: <VolunteerDetailsPage />,
        },
      ]),
];

const b2bProfileRouter = [
  { index: true, element: <Navigate to={paths.profile.general} /> },
  { index: true, path: paths.profile.general, element: <GeneralFormPage /> },
  { index: true, path: paths.profile.password, element: <PasswordFormPage /> },
];

const b2bHospitalsRouter = [
  { index: true, element: <HospitalsListPage /> },
  { path: ':hospitalId', element: <HospitalDetailsPage /> },
  { index: true, path: paths.create, element: <CreateHospital /> },
];

const b2bDiseasesRouter = [
  { index: true, element: <DiseasesListPage /> },
  { path: ':diseaseId', element: <DiseaseDetailsPage /> },
];

const b2bQuestionsLibraryRouter = (role: Role) => [
  { index: true, element: <QuestionsLibraryPage /> },
  ...(role === Role.Admin
    ? [{ path: paths.questions_library.create, element: <CreateQuestionPage /> }]
    : []),
  {
    path: ':questionId',
    element: <QuestionPage />,
    children: [
      { index: true, element: <Navigate to={paths.questions_library.details} /> },
      {
        path: paths.questions_library.details,
        element: <QuestionDetailsPage />,
      },
      {
        path: paths.questions_library.classification.root,
        element: <QuestionClassificationPage />,
        children: [
          {
            index: true,
            element: <Navigate to={paths.questions_library.classification.icd_codes} />,
          },
          {
            path: paths.questions_library.classification.icd_codes,
            element: <QuestionIcdCodesPage />,
          },
          {
            path: paths.questions_library.classification.tags,
            element: <QuestionTagsPage />,
          },
          {
            path: paths.questions_library.classification.starters,
            element: <QuestionStartersPage />,
          },
        ],
      },
      {
        path: paths.questions_library.trials,
        element: <QuestionTrialsPage />,
      },
      {
        path: paths.questions_library.merge,
        element: <QuestionMergePage />,
      },
      {
        path: paths.questions_library.activity,
        element: <QuestionActivityPage />,
      },
    ],
  },
  ...(role === Role.Admin
    ? [
        {
          path: `:questionId/${paths.questions_library.merge}/:mergeId`,
          element: <QuestionMergeEditorPage />,
        },
      ]
    : []),
];

const b2bParityRoutingRouter = [
  { index: true, element: <Navigate to={paths.parity_routing.routing} replace /> },
  { path: paths.parity_routing.routing, element: <ParityRoutingTablePage /> },
  { path: paths.parity_routing.history, element: <ParityRoutingHistoryPage /> },
];

const b2bPredicateLibraryRouter = [
  { index: true, element: <Navigate to={paths.predicate_library.library} replace /> },
  { path: paths.predicate_library.library, element: <PredicateLibraryBrowsePage /> },
  { path: paths.predicate_library.missing, element: <PredicateLibraryMissingPage /> },
  { path: paths.predicate_library.tiers, element: <PredicateLibraryTiersPage /> },
];

const b2bParityReviewRouter = [
  { index: true, element: <Navigate to={paths.parity_review.slices} replace /> },
  { path: paths.parity_review.slices, element: <ParityReviewSlicesPage /> },
  { path: paths.parity_review.trials, element: <ParityReviewTrialsPage /> },
  { path: paths.parity_review.matched_sets, element: <ParityReviewMatchedSetsPage /> },
  { path: paths.parity_review.readiness, element: <ParityReviewReadinessPage /> },
];

const b2bPartnerReportingRouter = [
  { index: true, element: <Navigate to={paths.partner_reporting.overview} replace /> },
  { path: paths.partner_reporting.overview, element: <PartnerReportingOverviewTab /> },
  {
    path: paths.partner_reporting.trials_overview,
    element: <PartnerReportingTrialsOverviewTab />,
  },
  {
    path: paths.partner_reporting.trial_geography,
    element: <PartnerReportingTrialGeographyTab />,
  },
  {
    path: paths.partner_reporting.trial_criteria,
    element: <PartnerReportingTrialCriteriaTab />,
  },
  {
    path: paths.partner_reporting.trial_rejections,
    element: <PartnerReportingTrialRejectionsTab />,
  },
  {
    path: paths.partner_reporting.site_opportunity,
    element: <PartnerReportingSiteOpportunityTab />,
  },
  { path: paths.partner_reporting.trial_delays, element: <PartnerReportingTrialDelaysTab /> },
  {
    path: paths.partner_reporting.criteria_simulator,
    element: <PartnerReportingCriteriaSimulatorTab />,
  },
  { path: paths.partner_reporting.chatbot, element: <PartnerReportingChatbotTab /> },
  {
    path: paths.partner_reporting.patients_overview,
    element: <PartnerReportingPatientsOverviewTab />,
  },
  // The retired team-scoped "User overview" tab; a bookmark lands on its global successor.
  {
    path: 'user-overview',
    element: <Navigate to={`../${paths.partner_reporting.patients_overview}`} replace />,
  },
  { path: paths.partner_reporting.funnel, element: <PartnerReportingFunnelTab /> },
];

const b2bUnitsLibraryRouter = [
  { index: true, element: <Navigate to={paths.units.standard_units} replace /> },
  { path: paths.units.standard_units, element: <UnitsStandardUnitsPage /> },
  { path: paths.units.active, element: <UnitsActiveRulesPage /> },
  { path: paths.units.queue, element: <UnitsConversionQueuePage /> },
  { path: paths.units.rejected, element: <UnitsRejectedRulesPage /> },
];

const b2bTrialsLibraryRouter = [
  { index: true, element: <TrialsLibraryPage /> },
  { path: ':trialId', element: <TrialDetailsPage /> },
];

const b2bSurveysRouter = [
  { index: true, element: <SurveysPage /> },
  {
    path: paths.surveys.create,
    element: <CreateSurveyPage />,
  },
  {
    path: ':surveyId',
    element: <SurveyPage />,
    children: [
      {
        index: true,
        element: <Navigate to={paths.surveys.details} />,
      },
      {
        path: paths.surveys.details,
        element: <UpdateSurveyDetails />,
      },
      {
        path: paths.surveys.questions,
        element: <SurveyQuestionsPage />,
      },
      {
        path: paths.surveys.responses,
        element: <SurveyResponsesPage />,
      },
    ],
  },
];

const b2bPanelsRouter = [
  { index: true, element: <PanelsPage /> },
  { path: paths.panels.create, element: <CreatePanelPage /> },
  {
    path: ':panelId',
    element: <PanelPage />,
    children: [
      {
        index: true,
        element: <Navigate to={paths.panels.details} />,
      },
      {
        path: paths.panels.details,
        element: <PanelDetailsPage />,
      },
      {
        path: paths.panels.insights,
        element: <PanelInsightsPage />,
      },
      {
        path: `${paths.panels.insights}/:patientId`,
        element: <PatientDetailsPage />,
      },
    ],
  },
];

const b2bRouter = (currentRole?: Role) => [
  {
    element: <B2BLayout />,
    children: [
      {
        index: true,
        element: <Navigate replace to={paths.trial_recruitment} />,
      },
      { index: true, path: paths.create_team, element: <CreateTeam /> },
      {
        path: paths.trial_recruitment,
        children: b2bTrialRecruitmentRouter(currentRole),
      },
      // Open to every B2B role, like the live trial page: its loader fails for a trial the user
      // cannot read.
      {
        path: `${paths.trials_redesign.root}/${paths.trials_redesign.trial}`,
        element: <TrialsRedesignPage />,
        loader: trialsRedesignLoader(queryClient),
        children: [
          { index: true, element: <Navigate to={paths.trials_redesign.overview} replace /> },
          { path: paths.trials_redesign.overview, element: <TrialsRedesignOverviewTab /> },
          { path: paths.trials_redesign.criteria, element: <TrialsRedesignCriteriaTab /> },
          { path: paths.trials_redesign.sites, element: <TrialsRedesignSitesTab /> },
          {
            path: paths.trials_redesign.matching_patients,
            element: <TrialsRedesignPatientsTab />,
          },
          { path: paths.trials_redesign.details, element: <TrialsRedesignDetailsTab /> },
          ...(currentRole === Role.Admin ||
          currentRole === Role.TrialManager ||
          currentRole === Role.Sponsor
            ? [{ path: paths.trials_redesign.updates, element: <TrialUpdatesPage /> }]
            : []),
          ...(currentRole === Role.Admin
            ? [
                { path: paths.trials_redesign.teams, element: <AdminTrialTeams /> },
                { path: paths.trials_redesign.companion, element: <MilestonesPage /> },
                {
                  path: `${paths.trials_redesign.companion}/${paths.create}`,
                  element: <MilestoneCreatePage />,
                },
                {
                  path: `${paths.trials_redesign.companion}/:milestoneId`,
                  element: <MilestoneDetailsPage />,
                },
              ]
            : []),
        ],
      },
      {
        path: paths.profile.root,
        element: <B2BProfileLayout />,
        children: b2bProfileRouter,
      },
      {
        index: true,
        path: paths.notifications,
        element: <Notifications />,
      },
      ...(currentRole === Role.Admin || currentRole === Role.Reviewer
        ? [
            { path: paths.hospitals, children: b2bHospitalsRouter },
            { path: paths.diseases, children: b2bDiseasesRouter },
            {
              path: paths.questions_library.root,
              children: b2bQuestionsLibraryRouter(currentRole),
            },
            {
              path: paths.pipeline.root,
              children: [
                { index: true, element: <PipelineListPage /> },
                { path: paths.pipeline.trial, element: <PipelineTrialPage /> },
              ],
            },
            {
              path: paths.trials_library,
              children: b2bTrialsLibraryRouter,
            },
            {
              path: paths.team.root,
              children: [
                { index: true, element: <TeamsList /> },
                ...(currentRole === Role.Admin
                  ? [
                      {
                        path: ':teamId',
                        element: <TeamSettingsLayout />,
                        children: [
                          { index: true, element: <Navigate to={paths.team.general} /> },
                          { path: paths.team.general, element: <TeamGeneral /> },
                          { path: paths.team.trials, element: <TeamTrials /> },
                          { path: paths.team.panels, element: <TeamPanels /> },
                          { path: paths.team.users, element: <TeamUsers /> },
                          {
                            path: paths.team.reporting_scope,
                            element: <TeamReportingScope />,
                          },
                        ],
                      },
                    ]
                  : []),
              ],
            },
            {
              path: paths.users,
              element: <UsersPage />,
            },
            {
              path: paths.predicate_questions.root,
              children: [
                { index: true, element: <PredicateQuestionsPage /> },
                {
                  path: paths.predicate_questions.question,
                  element: <PredicateQuestionDetailPage />,
                },
              ],
            },
            {
              path: paths.predicate_library.root,
              element: <PredicateLibraryPage />,
              children: b2bPredicateLibraryRouter,
            },
            {
              // Standalone detail page — intentionally outside the tabbed shell, which is a
              // switch between three lists and says nothing about one category.
              path: `${paths.predicate_library.root}/${paths.predicate_library.category}`,
              element: <PredicateLibraryItemDetailPage />,
            },
            {
              path: paths.units.root,
              element: <UnitsPage />,
              children: b2bUnitsLibraryRouter,
            },
            {
              // Standalone detail page — intentionally outside the tabbed <UnitsPage> shell.
              path: `${paths.units.root}/${paths.units.standard_unit_detail}`,
              element: <UnitsStandardUnitDetailPage />,
            },
            // Admin-or-Reviewer like its endpoints: this page only reads. Parity Routing, which
            // writes the stages this page reviews, stays Admin-only further down.
            {
              path: paths.parity_review.root,
              element: <ParityReviewPage />,
              children: b2bParityReviewRouter,
            },
            // PROTOTYPES — gated via prototypes/registry.ts feature flags
            ...enabledPrototypeRoutes('predicates-submenu'),
          ]
        : []),
      ...(currentRole === Role.TrialManager ||
      currentRole === Role.PatientOrg ||
      currentRole === Role.Sponsor
        ? [
            {
              path: `${paths.team.root}/:teamId`,
              element: <TeamSettingsLayout />,
              // TODO: add loader to prefetch team info for general form
              children: [
                { index: true, element: <Navigate to={paths.team.general} /> },
                { index: true, path: paths.team.general, element: <TeamGeneral /> },
                { index: true, path: paths.team.users, element: <TeamUsers /> },
                ...(currentRole === Role.TrialManager || currentRole === Role.Sponsor
                  ? [
                      { index: true, path: paths.team.billing, element: <TeamBilling /> },
                      { index: true, path: paths.team.premium, element: <TeamPremium /> },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(currentRole === Role.TrialManager ||
      currentRole === Role.Sponsor ||
      currentRole === Role.PatientOrg
        ? [
            { path: paths.onboarding.root, element: <OnboardingPage /> },
            { path: paths.surveys.root, element: <SurveyLanding /> },
            { path: paths.panels.root, element: <PanelsAccessLayout />, children: b2bPanelsRouter },
          ]
        : []),
      // The retired Insight v1 embed lived here; a bookmark of it lands on Insights.
      {
        path: paths.insights,
        element: <Navigate to={`../${paths.partner_reporting.root}`} replace />,
      },
      {
        path: paths.partner_reporting.root,
        element: <PartnerReportingPage />,
        children: b2bPartnerReportingRouter,
      },
      // PROTOTYPES ('top-level' placement) — reachable by every B2B role, same as Insights
      // above, unlike the 'predicates-submenu' prototypes gated to
      // Admin/Reviewer further up.
      ...enabledPrototypeRoutes('top-level'),
      ...(currentRole === Role.PatientOrg
        ? [{ path: paths.diseases, children: b2bDiseasesRouter }]
        : []),
      ...(currentRole === Role.Admin
        ? [
            { path: paths.surveys.root, children: b2bSurveysRouter },
            { path: paths.panels.root, children: b2bPanelsRouter },
            // Admin-only, not Admin-or-Reviewer: the history endpoint allows Reviewer,
            // but the page as a whole is an editing surface.
            {
              path: paths.parity_routing.root,
              element: <ParityRoutingPage />,
              children: b2bParityRoutingRouter,
            },
          ]
        : []),
    ],
  },
];

const publicPages = [
  {
    index: true,
    path: paths.trial_invite,
    element: <TrialInvitePage />,
  },
  {
    index: true,
    path: paths.team_trial_invite,
    element: <TeamTrialInvitePage />,
  },
  {
    index: true,
    path: paths.sign_in,
    element: <SignInPage />,
  },
  // TODO: fix this after we confirm new sign up works
  // {
  //   index: true,
  //   path: paths.sign_up,
  //   element: <SignUpPage />,
  // },
  {
    index: true,
    path: paths.setup_password,
    element: <SetupPasswordPage />,
  },
  {
    index: true,
    path: paths.reset_password,
    element: <ForgotPasswordPage />,
  },
  {
    index: true,
    path: paths.unsubscribe,
    element: <UnsubscribePage />,
  },
];

export const router = (isOptimisticSignUpInProgress: boolean, currentRole?: Role) =>
  createBrowserRouter([
    {
      path: paths.base,
      index: true,
      errorElement: <NoMatchPage />,
      element: <Navigate to={paths.sign_in} />,
    },
    {
      path: `${paths.base}${paths.maintenance}`,
      index: true,
      element: <MaintenancePage />,
    },
    {
      path: paths.base,
      errorElement: <NoMatchPage />,
      element: <PublicPageLayout />,
      children: publicPages,
    },
    {
      path: paths.sign_up,
      element: <PublicPageLayoutV2 />,
      children: [{ index: true, element: <SignUpV2Page /> }],
    },
    {
      path: `${paths.b2b}/${paths.sign_up}`,
      element: <PublicPageLayoutV2 />,
      children: [{ index: true, element: <TrialManagerSignUpPage /> }],
    },
    {
      path: paths.embedded_chat,
      errorElement: <NoMatchPage />,
      element: <EmbeddedChatPage />,
    },
    {
      path: paths.embedded_chat_v2,
      errorElement: <NoMatchPage />,
      element: <EmbeddedChatV2Page />,
    },
    {
      index: true,
      path: `${paths.milestone}/:token`,
      element: <MilestoneInvitePage />,
    },
    {
      path: paths.select_account,
      element: <PrivateRoute allowedRoles={[Role.Volunteer]} />,
      children: [{ index: true, element: <AccountSelectPage /> }],
    },
    {
      path: paths.app,
      errorElement: <NoMatchPage />,
      element: (
        <PrivateRoute
          allowedRoles={[
            Role.Admin,
            Role.TrialManager,
            Role.Reviewer,
            Role.PatientOrg,
            Role.Volunteer,
            Role.Sponsor,
          ]}
        />
      ),
      children:
        currentRole === Role.Volunteer || isOptimisticSignUpInProgress
          ? patientRouter(isOptimisticSignUpInProgress)
          : b2bRouter(currentRole),
    },
    {
      path: paths.no_match,
      // No `from` here: the path matched no route, so restoring it after sign-in would land back
      // on this catch-all and loop.
      element: <Navigate to={paths.sign_in} />,
    },
    {
      path: paths.ctdi,
      element: <CTDIPage />,
    },
  ]);
